import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Search, Filter, Clock, User, FileText, ArrowUpRight } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button } from "../../components/common/Primitives";
import api, { formatApiError } from "../../lib/api";

function relTime(iso) {
  if (!iso) return "";
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export default function DownloadForensics() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    doc_id: "",
    deal_room_id: "",
    downloaded_by_email: "",
    days: 30,
  });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const { data } = await api.get(`/admin/document-downloads?${params.toString()}`);
      setRows(data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set("export_csv", "true");
      const resp = await api.get(`/admin/document-downloads?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `download-forensics-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totals = useMemo(() => {
    const byUser = {};
    const byDoc = {};
    rows.forEach((r) => {
      byUser[r.downloaded_by_email] = (byUser[r.downloaded_by_email] || 0) + 1;
      byDoc[r.doc_id] = (byDoc[r.doc_id] || 0) + 1;
    });
    return { unique_users: Object.keys(byUser).length, unique_docs: Object.keys(byDoc).length };
  }, [rows]);

  return (
    <DashboardShell
      title="Download Forensics"
      subtitle="Every diligence PDF that leaves the platform. Filter by doc, deal room, user or date range. Every row is anchored by a unique trace ID that also appears on the downloaded file."
    >
      <div className="space-y-5">
        <Card testId="forensics-filters">
          <div className="overline mb-3 flex items-center gap-1.5"><Filter size={12} className="text-gold" /> Filters</div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <input
              type="text" placeholder="Doc ID"
              value={filters.doc_id}
              onChange={(e) => setFilters({ ...filters, doc_id: e.target.value })}
              className="border border-navy/20 rounded px-3 py-1.5 text-sm bg-white"
              data-testid="forensics-filter-doc-id"
            />
            <input
              type="text" placeholder="Deal room ID"
              value={filters.deal_room_id}
              onChange={(e) => setFilters({ ...filters, deal_room_id: e.target.value })}
              className="border border-navy/20 rounded px-3 py-1.5 text-sm bg-white"
              data-testid="forensics-filter-dr"
            />
            <input
              type="text" placeholder="Downloader email"
              value={filters.downloaded_by_email}
              onChange={(e) => setFilters({ ...filters, downloaded_by_email: e.target.value })}
              className="border border-navy/20 rounded px-3 py-1.5 text-sm bg-white"
              data-testid="forensics-filter-email"
            />
            <select
              value={filters.days}
              onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value, 10) })}
              className="border border-navy/20 rounded px-2 py-1.5 text-sm bg-white"
              data-testid="forensics-filter-days"
            >
              <option value={1}>Last 24h</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last year</option>
            </select>
            <div className="flex items-center gap-2">
              <Button onClick={load} data-testid="forensics-apply-btn" className="flex-1">
                <Search size={12} className="inline mr-1" /> Apply
              </Button>
              <Button variant="dark" onClick={exportCsv} data-testid="forensics-export-btn">
                <Download size={12} className="inline mr-1" /> CSV
              </Button>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile label="Downloads" value={rows.length} />
          <StatTile label="Unique users" value={totals.unique_users} />
          <StatTile label="Unique docs" value={totals.unique_docs} />
          <StatTile label="Range" value={`${filters.days}d`} />
        </div>

        <Card testId="forensics-table">
          <div className="overline mb-3">Downloads · {loading ? "loading…" : `${rows.length} rows`}</div>
          {rows.length === 0 && !loading && (
            <div className="text-sm text-ink-muted italic text-center py-10">
              <FileText size={22} className="text-ink-muted mx-auto mb-2 opacity-50" />
              No downloads in this range.
            </div>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                  <tr>
                    <th className="text-left py-2 pr-3">When</th>
                    <th className="text-left py-2 pr-3">By</th>
                    <th className="text-left py-2 pr-3">Role</th>
                    <th className="text-left py-2 pr-3">Deal Room</th>
                    <th className="text-left py-2 pr-3">Doc</th>
                    <th className="text-left py-2 pr-3">Type</th>
                    <th className="text-left py-2 pr-3">Trace ID</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.log_id || r.trace_id} className="border-t border-navy/5 hover:bg-gold/[0.03]" data-testid={`forensics-row-${r.trace_id}`}>
                      <td className="py-2 pr-3">
                        <div className="text-ink whitespace-nowrap">{relTime(r.downloaded_at)}</div>
                        <div className="text-[10px] font-mono text-ink-muted whitespace-nowrap">{r.downloaded_at?.slice(0, 16).replace("T", " ")}</div>
                      </td>
                      <td className="py-2 pr-3 text-ink font-mono">{r.downloaded_by_email}</td>
                      <td className="py-2 pr-3"><Pill tone="muted">{r.downloaded_by_role || "—"}</Pill></td>
                      <td className="py-2 pr-3 text-ink font-mono">{r.deal_room_id}</td>
                      <td className="py-2 pr-3 text-ink font-mono">{r.doc_id}</td>
                      <td className="py-2 pr-3 text-ink">{(r.doc_type || "").replace(/_/g, " ")}</td>
                      <td className="py-2 pr-3 text-[10px] font-mono text-[#8A6600]" title="Matches the trace ID stamped on the downloaded PDF footer">
                        {r.trace_id}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </DashboardShell>
  );
}

function StatTile({ label, value }) {
  return (
    <Card testId={`forensics-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{label}</div>
      <div className="text-2xl font-heading text-ink mt-1">{value}</div>
    </Card>
  );
}
